/**
 * Reusable Form Components
 *
 * A collection of form input components with built-in validation and consistent styling.
 * These components follow the Have-Done design system and provide accessible,
 * validated form fields.
 */

import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes } from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Validation rule type for form fields
 */
export interface ValidationRule {
  /** Validation function that returns true if valid */
  validate: (value: string) => boolean;
  /** Error message to display when validation fails */
  message: string;
}

/**
 * Common validation rules for reuse across forms
 */
export const validationRules = {
  required: (fieldName: string): ValidationRule => ({
    validate: (value) => value.trim().length > 0,
    message: `${fieldName} is required`,
  }),

  email: (): ValidationRule => ({
    validate: (value) => {
      if (!value.trim()) return true; // Let required rule handle empty
      // RFC 5322 compliant email regex (simplified)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value.trim());
    },
    message: 'Please enter a valid email address',
  }),

  minLength: (min: number, fieldName: string): ValidationRule => ({
    validate: (value) => {
      if (!value.trim()) return true; // Let required rule handle empty
      return value.trim().length >= min;
    },
    message: `${fieldName} must be at least ${min} characters`,
  }),

  maxLength: (max: number, fieldName: string): ValidationRule => ({
    validate: (value) => value.length <= max,
    message: `${fieldName} must be no more than ${max} characters`,
  }),
};

/**
 * Validates a value against an array of rules
 * @returns Error message if validation fails, undefined if valid
 */
export function validateField(value: string, rules: ValidationRule[]): string | undefined {
  for (const rule of rules) {
    if (!rule.validate(value)) {
      return rule.message;
    }
  }
  return undefined;
}

/**
 * Base styles for form inputs
 */
const baseInputStyles = `
  w-full px-4 py-3 rounded-lg border bg-bg-primary text-text-primary
  placeholder:text-text-tertiary
  focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
  transition-all
`;

const errorInputStyles = 'border-error focus:ring-error';
const normalInputStyles = 'border-border-light';

/**
 * Props for form field wrapper
 */
interface FormFieldProps {
  /** Label text for the field */
  label: string;
  /** HTML id for the input (also used for label's htmlFor) */
  id: string;
  /** Error message to display */
  error?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Helper text to display below the field */
  helperText?: string;
  /** Children (the actual input element) */
  children: React.ReactNode;
}

/**
 * FormField - Wrapper component for form inputs with label and error handling
 */
export function FormField({ label, id, error, required, helperText, children }: FormFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-text-primary mb-2">
        {label}
        {required && <span className="text-error ml-1">*</span>}
      </label>
      {children}
      {error && (
        <div className="flex items-center gap-1.5 mt-1.5 text-error">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span className="text-xs">{error}</span>
        </div>
      )}
      {helperText && !error && (
        <p className="text-xs text-text-tertiary mt-1">{helperText}</p>
      )}
    </div>
  );
}

/**
 * Props for FormInput component
 */
interface FormInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  /** Label text for the field */
  label: string;
  /** Error message to display */
  error?: string;
  /** Helper text to display below the field */
  helperText?: string;
}

/**
 * FormInput - Text/email/etc input with label and error handling
 */
export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ label, error, helperText, required, id, ...props }, ref) => {
    const inputId = id || props.name || '';

    return (
      <FormField label={label} id={inputId} error={error} required={required} helperText={helperText}>
        <input
          ref={ref}
          id={inputId}
          className={`${baseInputStyles} ${error ? errorInputStyles : normalInputStyles}`}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
          required={required}
          {...props}
        />
      </FormField>
    );
  }
);
FormInput.displayName = 'FormInput';

/**
 * Props for FormTextarea component
 */
interface FormTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  /** Label text for the field */
  label: string;
  /** Error message to display */
  error?: string;
  /** Helper text to display below the field */
  helperText?: string;
  /** Whether to show character count */
  showCharCount?: boolean;
  /** Maximum character count (for display purposes) */
  maxChars?: number;
}

/**
 * FormTextarea - Textarea with label, error handling, and optional character count
 */
export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  ({ label, error, helperText, required, id, showCharCount, maxChars, value, ...props }, ref) => {
    const textareaId = id || props.name || '';
    const charCount = typeof value === 'string' ? value.length : 0;

    return (
      <FormField label={label} id={textareaId} error={error} required={required}>
        <textarea
          ref={ref}
          id={textareaId}
          className={`${baseInputStyles} resize-none ${error ? errorInputStyles : normalInputStyles}`}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${textareaId}-error` : undefined}
          required={required}
          value={value}
          {...props}
        />
        {(showCharCount || helperText) && !error && (
          <p className="text-xs text-text-tertiary mt-1">
            {showCharCount && maxChars && `${charCount}/${maxChars} characters`}
            {showCharCount && maxChars && helperText && ' - '}
            {helperText}
          </p>
        )}
      </FormField>
    );
  }
);
FormTextarea.displayName = 'FormTextarea';

/**
 * Option type for FormSelect
 */
export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Props for FormSelect component
 */
interface FormSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  /** Label text for the field */
  label: string;
  /** Options to display in the select */
  options: SelectOption[];
  /** Error message to display */
  error?: string;
  /** Helper text to display below the field */
  helperText?: string;
}

/**
 * FormSelect - Select dropdown with label and error handling
 */
export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  ({ label, options, error, helperText, required, id, ...props }, ref) => {
    const selectId = id || props.name || '';

    return (
      <FormField label={label} id={selectId} error={error} required={required} helperText={helperText}>
        <select
          ref={ref}
          id={selectId}
          className={`${baseInputStyles} appearance-none cursor-pointer pr-10 text-ellipsis ${error ? errorInputStyles : normalInputStyles}`}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${selectId}-error` : undefined}
          required={required}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B6770'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 12px center',
            backgroundSize: '20px',
          }}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.description ? `${option.label} - ${option.description}` : option.label}
            </option>
          ))}
        </select>
      </FormField>
    );
  }
);
FormSelect.displayName = 'FormSelect';

/**
 * Props for FormError component (for global form errors)
 */
interface FormErrorProps {
  /** Error message to display */
  message: string;
}

/**
 * FormError - Global form error message display
 */
export function FormError({ message }: FormErrorProps) {
  return (
    <div className="p-4 rounded-lg bg-error/10 border border-error/20">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-error shrink-0 mt-0.5" />
        <p className="text-sm text-error">{message}</p>
      </div>
    </div>
  );
}

/**
 * Props for FormSuccess component
 */
interface FormSuccessProps {
  /** Title of the success message */
  title: string;
  /** Description of the success message */
  description?: string;
  /** Icon to display (optional, defaults to checkmark) */
  icon?: React.ReactNode;
}

/**
 * FormSuccess - Success message display
 */
export function FormSuccess({ title, description, icon }: FormSuccessProps) {
  return (
    <div className="p-4 rounded-lg bg-success/10 border border-success/20">
      <div className="flex items-start gap-3">
        {icon || (
          <svg className="w-5 h-5 text-success shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        <div>
          <h3 className="font-medium text-text-primary">{title}</h3>
          {description && (
            <p className="text-sm text-text-secondary mt-1">{description}</p>
          )}
        </div>
      </div>
    </div>
  );
}
